/* ============================================================
   BLOQUE — global.js
   All theme interactivity, driven by data attributes.
   No dependencies. Sections opt in by using the documented
   data-bq-* hooks (see docs/CONVENTIONS.md).
   ============================================================ */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Reveal on scroll: [data-bq-reveal] ---------- */
  function initReveals(root) {
    var nodes = (root || document).querySelectorAll('[data-bq-reveal]:not(.bq-revealed)');
    if (!nodes.length) return;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('bq-revealed'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('bq-revealed');
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ---------- Marquee: [data-bq-marquee] ----------
     Duplicates the track until it fills 2x the viewport so the
     CSS loop is seamless regardless of content length. */
  function initMarquees(root) {
    (root || document).querySelectorAll('[data-bq-marquee]').forEach(function (m) {
      if (m.dataset.bqReady) return;
      m.dataset.bqReady = '1';
      var track = m.querySelector('.bq-marquee__track');
      if (!track || !track.children.length) return;
      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      m.appendChild(clone);
      var guard = 0;
      while (m.scrollWidth < window.innerWidth * 2 && guard < 6) {
        var extra = track.cloneNode(true);
        extra.setAttribute('aria-hidden', 'true');
        m.appendChild(extra);
        guard++;
      }
    });
  }

  /* ---------- Header: mobile drawer + cart count ---------- */
  function initHeader() {
    var toggle = document.querySelector('[data-bq-nav-toggle]');
    var drawer = document.querySelector('[data-bq-nav-drawer]');
    if (toggle && drawer) {
      toggle.addEventListener('click', function () {
        var open = drawer.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        document.documentElement.classList.toggle('bq-nav-open', open);
      });
      drawer.addEventListener('click', function (e) {
        if (e.target.tagName === 'A') {
          drawer.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
          document.documentElement.classList.remove('bq-nav-open');
        }
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
          drawer.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
          document.documentElement.classList.remove('bq-nav-open');
          toggle.focus();
        }
      });
    }
  }

  function refreshCartCount() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-bq-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.classList.toggle('is-empty', cart.item_count === 0);
        });
      })
      .catch(function () {});
  }

  /* ---------- Quick add: form[data-bq-quick-add] ---------- */
  function initQuickAdd(root) {
    (root || document).querySelectorAll('form[data-bq-quick-add]').forEach(function (form) {
      if (form.dataset.bqReady) return;
      form.dataset.bqReady = '1';
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('[type="submit"]');
        var original = btn ? btn.innerHTML : '';
        if (btn) { btn.setAttribute('aria-disabled', 'true'); btn.innerHTML = '&hellip;'; }
        fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(form),
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })
          .then(function (r) {
            if (!r.ok) throw new Error('add failed');
            return r.json();
          })
          .then(function () {
            if (btn) { btn.innerHTML = 'ADDED ✓'; }
            refreshCartCount();
            setTimeout(function () {
              if (btn) { btn.innerHTML = original; btn.removeAttribute('aria-disabled'); }
            }, 1600);
          })
          .catch(function () {
            if (btn) { btn.innerHTML = 'SOLD OUT?'; }
            setTimeout(function () {
              if (btn) { btn.innerHTML = original; btn.removeAttribute('aria-disabled'); }
            }, 1600);
          });
      });
    });
  }

  /* ---------- Quantity steppers: .bq-qty ---------- */
  function initQty(root) {
    (root || document).querySelectorAll('.bq-qty').forEach(function (q) {
      if (q.dataset.bqReady) return;
      q.dataset.bqReady = '1';
      var input = q.querySelector('input');
      q.addEventListener('click', function (e) {
        var b = e.target.closest('button');
        if (!b || !input) return;
        e.preventDefault();
        var v = parseInt(input.value, 10) || 0;
        var min = parseInt(input.min, 10) || 0;
        if (b.dataset.bqQty === 'plus') input.value = v + 1;
        else input.value = Math.max(min, v - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ---------- Sticky process: [data-bq-process] ----------
     Right-hand steps scroll; the sticky left display updates to
     the active step's index/label. Steps: [data-bq-step]. Display
     targets: [data-bq-process-num], [data-bq-process-label]. */
  function initProcess(root) {
    (root || document).querySelectorAll('[data-bq-process]').forEach(function (wrap) {
      if (wrap.dataset.bqReady) return;
      wrap.dataset.bqReady = '1';
      var steps = wrap.querySelectorAll('[data-bq-step]');
      var num = wrap.querySelector('[data-bq-process-num]');
      var label = wrap.querySelector('[data-bq-process-label]');
      if (!steps.length) return;
      function activate(step) {
        steps.forEach(function (s) { s.classList.toggle('is-active', s === step); });
        if (num) num.textContent = step.dataset.bqStepNum || '';
        if (label) label.textContent = step.dataset.bqStepLabel || '';
      }
      activate(steps[0]);
      if (!('IntersectionObserver' in window)) return;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) activate(e.target);
        });
      }, { rootMargin: '-45% 0px -45% 0px' });
      steps.forEach(function (s) { io.observe(s); });
    });
  }

  /* ---------- Sticky showcase: [data-bq-showcase] ----------
     Text panels scroll past a sticky media column; the media with
     matching data-bq-media index becomes visible. */
  function initShowcase(root) {
    (root || document).querySelectorAll('[data-bq-showcase]').forEach(function (wrap) {
      if (wrap.dataset.bqReady) return;
      wrap.dataset.bqReady = '1';
      var panels = wrap.querySelectorAll('[data-bq-panel]');
      var medias = wrap.querySelectorAll('[data-bq-media]');
      if (!panels.length || !medias.length) return;
      function show(i) {
        medias.forEach(function (m) {
          m.classList.toggle('is-active', m.dataset.bqMedia === String(i));
        });
        panels.forEach(function (p) {
          p.classList.toggle('is-active', p.dataset.bqPanel === String(i));
        });
      }
      show(panels[0].dataset.bqPanel);
      if (!('IntersectionObserver' in window)) return;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) show(e.target.dataset.bqPanel);
        });
      }, { rootMargin: '-40% 0px -40% 0px' });
      panels.forEach(function (p) { io.observe(p); });
    });
  }

  /* ---------- Before/after compare: [data-bq-compare] ----------
     Structure: wrapper > img.before + .bq-compare__after(img) + input[type=range]. */
  function initCompare(root) {
    (root || document).querySelectorAll('[data-bq-compare]').forEach(function (wrap) {
      if (wrap.dataset.bqReady) return;
      wrap.dataset.bqReady = '1';
      var range = wrap.querySelector('input[type="range"]');
      if (!range) return;
      function update() {
        wrap.style.setProperty('--bq-compare', range.value + '%');
      }
      range.addEventListener('input', update);
      update();
    });
  }

  /* ---------- Terminal typing: [data-bq-terminal] ----------
     Lines live in a <template data-bq-terminal-lines>, one per <span>. */
  function initTerminals(root) {
    (root || document).querySelectorAll('[data-bq-terminal]').forEach(function (term) {
      if (term.dataset.bqReady) return;
      term.dataset.bqReady = '1';
      var tpl = term.querySelector('template');
      var out = term.querySelector('[data-bq-terminal-out]');
      if (!tpl || !out) return;
      var lines = Array.prototype.map.call(
        tpl.content.querySelectorAll('span'),
        function (s) { return { text: s.textContent, cls: s.className || '' }; }
      );
      if (!lines.length) return;
      if (reducedMotion) {
        lines.forEach(function (l) {
          var d = document.createElement('div');
          d.textContent = l.text;
          if (l.cls) d.className = l.cls;
          out.appendChild(d);
        });
        return;
      }
      var li = 0;
      var started = false;
      function typeLine() {
        if (li >= lines.length) {
          if (term.dataset.bqLoop === 'true') {
            setTimeout(function () { out.innerHTML = ''; li = 0; typeLine(); }, 2600);
          }
          return;
        }
        var line = lines[li];
        var d = document.createElement('div');
        if (line.cls) d.className = line.cls;
        out.appendChild(d);
        var ci = 0;
        var speed = line.cls.indexOf('cmd') > -1 ? 42 : 8;
        (function tick() {
          d.textContent = line.text.slice(0, ci);
          ci++;
          out.scrollTop = out.scrollHeight;
          if (ci <= line.text.length) setTimeout(tick, speed);
          else { li++; setTimeout(typeLine, line.cls.indexOf('cmd') > -1 ? 350 : 120); }
        })();
      }
      function start() {
        if (started) return;
        started = true;
        typeLine();
      }
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) { if (e.isIntersecting) { start(); io.disconnect(); } });
        }, { threshold: 0.3 });
        io.observe(term);
      } else start();
    });
  }

  /* ---------- Count up: [data-bq-count] (value in attribute) ---------- */
  function initCounts(root) {
    var nodes = (root || document).querySelectorAll('[data-bq-count]');
    if (!nodes.length) return;
    function run(el) {
      var target = parseFloat(el.dataset.bqCount) || 0;
      var suffix = el.dataset.bqCountSuffix || '';
      if (reducedMotion) { el.textContent = target + suffix; return; }
      var t0 = null;
      var dur = 1100;
      function frame(t) {
        if (!t0) t0 = t;
        var p = Math.min((t - t0) / dur, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(target * eased) + suffix;
        if (p < 1) requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }
    if (!('IntersectionObserver' in window)) {
      nodes.forEach(run);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { run(e.target); io.unobserve(e.target); }
      });
    }, { threshold: 0.5 });
    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ---------- Product form: variant picker [data-bq-variants] ----------
     Selects with data-bq-option update the hidden variant id input,
     price display, and availability button state. Variant data comes
     from a JSON <script data-bq-variant-data> in the section. */
  function initVariantPickers(root) {
    (root || document).querySelectorAll('[data-bq-variants]').forEach(function (wrap) {
      if (wrap.dataset.bqReady) return;
      wrap.dataset.bqReady = '1';
      var dataEl = wrap.querySelector('script[data-bq-variant-data]');
      var idInput = wrap.querySelector('input[name="id"]');
      if (!dataEl || !idInput) return;
      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }
      var selects = wrap.querySelectorAll('[data-bq-option]');
      var priceEl = wrap.querySelector('[data-bq-price]');
      var btn = wrap.querySelector('[data-bq-add-btn]');
      function money(cents) {
        var fmt = wrap.dataset.bqMoneyFormat || '${{amount}}';
        var amount = (cents / 100).toFixed(2);
        return fmt.replace(/\{\{\s*amount\s*\}\}/, amount);
      }
      function current() {
        var opts = Array.prototype.map.call(selects, function (s) { return s.value; });
        return variants.find(function (v) {
          return opts.every(function (o, i) { return v.options[i] === o; });
        });
      }
      function update() {
        var v = current();
        if (!v) {
          if (btn) { btn.setAttribute('aria-disabled', 'true'); btn.textContent = 'UNAVAILABLE'; }
          return;
        }
        idInput.value = v.id;
        if (priceEl) priceEl.textContent = money(v.price);
        if (btn) {
          if (v.available) { btn.removeAttribute('aria-disabled'); btn.textContent = btn.dataset.bqAddLabel || 'ADD TO CART'; }
          else { btn.setAttribute('aria-disabled', 'true'); btn.textContent = 'SOLD OUT'; }
        }
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
      selects.forEach(function (s) { s.addEventListener('change', update); });
      if (btn) {
        wrap.addEventListener('submit', function (e) {
          if (btn.getAttribute('aria-disabled') === 'true') e.preventDefault();
        });
      }
    });
  }

  /* ---------- Product gallery: [data-bq-gallery] thumbs swap main image ---------- */
  function initGalleries(root) {
    (root || document).querySelectorAll('[data-bq-gallery]').forEach(function (g) {
      if (g.dataset.bqReady) return;
      g.dataset.bqReady = '1';
      var main = g.querySelector('[data-bq-gallery-main] img');
      g.addEventListener('click', function (e) {
        var t = e.target.closest('[data-bq-thumb]');
        if (!t || !main) return;
        e.preventDefault();
        main.src = t.dataset.bqThumb;
        if (t.dataset.bqThumbSrcset) main.srcset = t.dataset.bqThumbSrcset;
        if (t.querySelector('img')) main.alt = t.querySelector('img').alt || '';
        g.querySelectorAll('[data-bq-thumb]').forEach(function (x) {
          x.classList.toggle('is-active', x === t);
        });
      });
    });
  }

  /* ---------- Spotlight hero: [data-bq-spotlight] ----------
     Square cursor spotlight reveals an image layer beneath the text; a click
     drops "a pebble" — a circular wipe from the click point plus a water
     displacement pass (SVG feDisplacementMap) that settles to stillness.
     Modes: data-bq-spotlight-mode="settle" (image drains back after
     data-bq-spotlight-hold ms) | "stay" (second click drains).
     Contract (all inside the [data-bq-spotlight] section):
       [data-bq-spot-layer="hover"]  — image clipped to the cursor square
                                        (CSS reads --bq-sx / --bq-sy)
       [data-bq-spot-layer="reveal"] — image clipped by the expanding circle
                                        (CSS reads --bq-rx / --bq-ry)
       <filter> with feTurbulence + feDisplacementMap — the water
       [data-bq-spot-toggle]         — keyboard/touch button, aria-pressed
     State classes set here, styled by the section CSS:
       .is-scoping (hover spotlight active) · .is-revealed · .is-draining */
  function initSpotlights(root) {
    (root || document).querySelectorAll('[data-bq-spotlight]').forEach(function (sec) {
      if (sec.dataset.bqReady) return;
      sec.dataset.bqReady = '1';

      var hoverLayer = sec.querySelector('[data-bq-spot-layer="hover"]');
      var revealLayer = sec.querySelector('[data-bq-spot-layer="reveal"]');
      var toggle = sec.querySelector('[data-bq-spot-toggle]');
      if (!revealLayer) return;

      var filterEl = sec.querySelector('filter');
      var disp = filterEl ? filterEl.querySelector('feDisplacementMap') : null;
      var mode = sec.dataset.bqSpotlightMode === 'stay' ? 'stay' : 'settle';
      var hold = parseInt(sec.dataset.bqSpotlightHold, 10) || 4000;
      var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      var revealed = false;
      var settleTimer = null;
      var rippleRaf = null;

      function setVars(prefix, x, y) {
        sec.style.setProperty('--bq-' + prefix + 'x', x + 'px');
        sec.style.setProperty('--bq-' + prefix + 'y', y + 'px');
      }

      /* water: animate the displacement scale, then remove the filter so the
         settled image renders unfiltered (turbulence is expensive) */
      function water(peak, dur, done) {
        if (!disp || !filterEl || reducedMotion) { if (done) done(); return; }
        if (rippleRaf) cancelAnimationFrame(rippleRaf);
        revealLayer.style.filter = 'url(#' + filterEl.id + ')';
        var t0 = null;
        function frame(t) {
          if (!t0) t0 = t;
          var p = Math.min((t - t0) / dur, 1);
          /* rise fast, settle slow: sin ramp in the first fifth, cubic decay after */
          var amp = p < 0.2 ? Math.sin((p / 0.2) * Math.PI / 2) : Math.pow(1 - (p - 0.2) / 0.8, 3);
          disp.setAttribute('scale', String(peak * amp));
          if (p < 1) { rippleRaf = requestAnimationFrame(frame); }
          else {
            disp.setAttribute('scale', '0');
            revealLayer.style.filter = '';
            rippleRaf = null;
            if (done) done();
          }
        }
        rippleRaf = requestAnimationFrame(frame);
      }

      function point(e) {
        var r = sec.getBoundingClientRect();
        var x = e && typeof e.clientX === 'number' ? e.clientX - r.left : r.width / 2;
        var y = e && typeof e.clientY === 'number' ? e.clientY - r.top : r.height / 2;
        return { x: x, y: y };
      }

      function pebble(e) {
        var p = point(e);
        setVars('r', p.x, p.y);
        clearTimeout(settleTimer);
        if (revealed) { drain(); return; }
        revealed = true;
        sec.classList.remove('is-draining');
        sec.classList.add('is-revealed');
        sec.classList.remove('is-scoping');
        if (toggle) toggle.setAttribute('aria-pressed', 'true');
        water(46, 1300);
        if (mode === 'settle') settleTimer = setTimeout(drain, hold + 1300);
      }

      function drain() {
        if (!revealed) return;
        revealed = false;
        clearTimeout(settleTimer);
        sec.classList.add('is-draining');
        sec.classList.remove('is-revealed');
        if (toggle) toggle.setAttribute('aria-pressed', 'false');
        water(24, 800, function () { sec.classList.remove('is-draining'); });
        if (reducedMotion) sec.classList.remove('is-draining');
      }

      if (finePointer && hoverLayer && !reducedMotion) {
        var moveRaf = null;
        sec.addEventListener('pointermove', function (e) {
          if (revealed || moveRaf) return;
          moveRaf = requestAnimationFrame(function () {
            moveRaf = null;
            var p = point(e);
            setVars('s', p.x, p.y);
            sec.classList.add('is-scoping');
          });
        });
        sec.addEventListener('pointerleave', function () {
          sec.classList.remove('is-scoping');
        });
      }

      sec.addEventListener('click', function (e) {
        if (e.target.closest('a, button') && !e.target.closest('[data-bq-spot-toggle]')) return;
        pebble(e.target.closest('[data-bq-spot-toggle]') ? null : e);
      });

      /* editor: the unload event fires on the .shopify-section container and
         bubbles to document — it never passes through this inner section */
      document.addEventListener('shopify:section:unload', function (e) {
        if (e.target && e.target.contains && e.target.contains(sec)) {
          clearTimeout(settleTimer);
          if (rippleRaf) cancelAnimationFrame(rippleRaf);
        }
      });
    });
  }

  /* ---------- Showreel deck: [data-bq-deck] ----------
     A pile of window cards, shuffled like a deck:
       NEXT  — top card slides out and is tucked under the bottom
       PREV  — bottom card is pulled out and dropped on top
     Contract (inside the [data-bq-deck] wrapper):
       [data-bq-deck-stack]              the pile
       [data-bq-deck-card]               one card per project (DOM order = deck order)
       video[data-bq-deck-video]         optional per card — only the front card plays
       [data-bq-deck-prev/-next]         shuffle buttons
       [data-bq-deck-count]              JS writes "01 / 05" (front card's authored position)
       [data-bq-deck-info]               info chip wrapper; button [data-bq-deck-info-btn]
                                         toggles .is-open (hover/focus opening is CSS-only)
     JS owns per-card: --bq-deck-i (0 = front), z-index, aria-hidden/inert,
     and the .is-flying class while a card travels. CSS draws everything else. */
  function initDecks(root) {
    (root || document).querySelectorAll('[data-bq-deck]').forEach(function (deck) {
      if (deck.dataset.bqReady) return;
      deck.dataset.bqReady = '1';

      var stack = deck.querySelector('[data-bq-deck-stack]');
      if (!stack) return;
      var order = Array.prototype.slice.call(stack.querySelectorAll('[data-bq-deck-card]'));
      if (!order.length) return;

      var prevBtn = deck.querySelector('[data-bq-deck-prev]');
      var nextBtn = deck.querySelector('[data-bq-deck-next]');
      var countEl = deck.querySelector('[data-bq-deck-count]');
      var busy = false;
      var inView = true;
      /* a one-project deck still plays video and opens its info chip —
         it just has nothing to shuffle */
      var single = order.length < 2;
      var FLIGHT = 260; /* ms per phase; keep in sync with the section CSS */

      function pad2(n) { return (n < 10 ? '0' : '') + n; }

      function applyDeck() {
        order.forEach(function (card, i) {
          card.style.setProperty('--bq-deck-i', i);
          card.style.zIndex = String(order.length - i);
          var back = i !== 0;
          card.setAttribute('aria-hidden', back ? 'true' : 'false');
          try { card.inert = back; } catch (err) { /* inert unsupported: aria-hidden covers it */ }
        });
        if (countEl) {
          var front = order[0];
          var pos = parseInt(front.dataset.bqIndex, 10);
          if (!(pos >= 0)) pos = 0; /* NaN guard */
          countEl.textContent = pad2(pos + 1) + ' / ' + pad2(order.length);
        }
        syncVideos();
      }

      function syncVideos() {
        order.forEach(function (card, i) {
          var v = card.querySelector('video[data-bq-deck-video]');
          if (!v) return;
          if (i === 0 && inView && !reducedMotion) {
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
          } else {
            v.pause();
          }
        });
      }

      function closeInfos() {
        deck.querySelectorAll('[data-bq-deck-info].is-open').forEach(function (n) {
          n.classList.remove('is-open');
          var b = n.querySelector('[data-bq-deck-info-btn]');
          if (b) b.setAttribute('aria-expanded', 'false');
        });
      }

      /* two-phase flight: lift out (class while in the old slot), reorder,
         settle into the new slot, then land */
      function shuffle(dir) {
        if (busy || single) return;
        busy = true;
        closeInfos();
        var card = dir === 'next' ? order[0] : order[order.length - 1];
        if (reducedMotion) {
          if (dir === 'next') order.push(order.shift());
          else order.unshift(order.pop());
          applyDeck();
          busy = false;
          return;
        }
        card.classList.add('is-flying');
        setTimeout(function () {
          if (dir === 'next') order.push(order.shift());
          else order.unshift(order.pop());
          applyDeck();
          setTimeout(function () {
            card.classList.remove('is-flying');
            busy = false;
          }, FLIGHT);
        }, FLIGHT);
      }

      if (nextBtn) nextBtn.addEventListener('click', function () { shuffle('next'); });
      if (prevBtn) prevBtn.addEventListener('click', function () { shuffle('prev'); });

      deck.addEventListener('keydown', function (e) {
        if (e.target.matches('input, textarea, select')) return;
        if (e.key === 'ArrowRight') { e.preventDefault(); shuffle('next'); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); shuffle('prev'); }
        if (e.key === 'Escape') {
          var open = deck.querySelector('[data-bq-deck-info].is-open');
          if (open) {
            closeInfos();
            var b = open.querySelector('[data-bq-deck-info-btn]');
            if (b) b.focus();
          }
        }
      });

      /* info chip: click/tap toggle (hover-open lives in CSS for fine pointers) */
      deck.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-bq-deck-info-btn]');
        if (!btn) return;
        var wrap = btn.closest('[data-bq-deck-info]');
        if (!wrap) return;
        var open = !wrap.classList.contains('is-open');
        closeInfos();
        wrap.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      var outsideClose = function (e) {
        if (!deck.contains(e.target)) closeInfos();
      };
      document.addEventListener('click', outsideClose);

      /* pause everything off-screen; resume the front card when back */
      var io = null;
      if ('IntersectionObserver' in window) {
        io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            inView = en.isIntersecting;
            syncVideos();
          });
        }, { threshold: 0.15 });
        io.observe(deck);
      }

      /* editor: unload fires on the .shopify-section container — drop the
         document listener and observer so reloads don't accumulate them */
      document.addEventListener('shopify:section:unload', function (e) {
        if (e.target && e.target.contains && e.target.contains(deck)) {
          document.removeEventListener('click', outsideClose);
          if (io) io.disconnect();
        }
      });

      applyDeck();
    });
  }

  /* ---------- Preselect saved values: select[data-bq-default] ----------
     Used by the customer address forms, where all_country_option_tags
     emits no selected option. Matches by option value, then text. */
  function initDefaultSelects(root) {
    (root || document).querySelectorAll('select[data-bq-default]').forEach(function (sel) {
      var want = sel.dataset.bqDefault;
      if (!want) return;
      var match = Array.prototype.find.call(sel.options, function (o) {
        return o.value === want || o.textContent.trim() === want;
      });
      if (match) sel.value = match.value;
    });
  }

  /* ---------- Boot ---------- */
  function boot(root) {
    initReveals(root);
    initMarquees(root);
    initQuickAdd(root);
    initQty(root);
    initProcess(root);
    initShowcase(root);
    initCompare(root);
    initTerminals(root);
    initCounts(root);
    initVariantPickers(root);
    initGalleries(root);
    initDefaultSelects(root);
    initSpotlights(root);
    initDecks(root);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initHeader();
    refreshCartCount();
    boot(document);
  });

  /* Re-init inside the Shopify theme editor when sections reload */
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });
})();
